/**
 * Display image URLs for `<img src>` / Next Image.
 *
 * - **Our media** (API-signed CloudFront / S3): load directly from the CDN.
 * - **Third-party** (Envato, Imgur, Google avatars): same-origin `/api/image-proxy`.
 */
import { getPublicMediaUrl, getS3ObjectKeyFromUrl } from "@/lib/utils/media-url";

const DEFAULT_CLOUDFRONT_HOST = "d28v02rpv54mir.cloudfront.net";

/** Host substrings for marketplace listing images (must stay in sync with `image-proxy/route.ts`). */
const MARKETPLACE_IMG_PROXY_INDICATORS = [
	"envatousercontent.com",
	"themeforest.imgix.net",
	"codecanyon.imgix.net",
	"preview-themeforest.imgix.net",
];

/** Hotlinked listing images (Imgur badges, etc.) — load via same-origin proxy in admin editor. */
const HOTLINK_IMG_PROXY_HOSTS = ["i.imgur.com", "imgur.com"];

const THIRD_PARTY_PROXY_INDICATORS = [
	"lh3.googleusercontent.com",
	...MARKETPLACE_IMG_PROXY_INDICATORS,
];

export const IMAGE_PROXY_FORMAT_VERSION = "web-safe-v2";
/** Resized listing/card images (~800px) — must match `image-proxy/route.ts` resize. */
export const LISTING_CARD_PROXY_FORMAT = "listing-card-w800";

export function buildImageProxyUrl(url: string): string;
export function buildImageProxyUrl(url: string, format: string): string;
export function buildImageProxyUrl(
	url: string,
	format: string = IMAGE_PROXY_FORMAT_VERSION,
): string {
	return `/api/image-proxy?url=${encodeURIComponent(url)}&format=${format}`;
}

export function isSignedMediaUrl(url: string | null | undefined): boolean {
	if (!url?.trim()) return false;
	try {
		const parsed = new URL(url.trim());
		return (
			parsed.searchParams.has("Signature") ||
			parsed.searchParams.has("X-Amz-Signature")
		);
	} catch {
		return false;
	}
}

export function isOurMediaUrl(url: string | null | undefined): boolean {
	if (!url?.trim()) return false;
	if (getS3ObjectKeyFromUrl(url) != null) return true;
	try {
		const host = new URL(url.trim()).hostname.toLowerCase();
		const configured = process.env.NEXT_PUBLIC_S3_MEDIA_BASE_URL?.trim();
		if (configured) {
			try {
				if (new URL(configured).hostname.toLowerCase() === host) return true;
			} catch {
				// ignore invalid env
			}
		}
		return host === DEFAULT_CLOUDFRONT_HOST;
	} catch {
		return false;
	}
}

/** True when the browser must use `/api/image-proxy` (third-party hotlink / CORS). */
export function needsImageProxy(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	if (isSignedMediaUrl(url) || isOurMediaUrl(url)) return false;

	const u = url.trim().toLowerCase();
	if (THIRD_PARTY_PROXY_INDICATORS.some((ind) => u.includes(ind))) return true;

	try {
		const host = new URL(url.trim()).hostname.toLowerCase();
		return HOTLINK_IMG_PROXY_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
	} catch {
		return false;
	}
}

function listingCardProxyForOurMedia(url: string): string {
	let canonical = url;
	if (isSignedMediaUrl(url)) {
		try {
			const parsed = new URL(url);
			canonical = `${parsed.origin}${parsed.pathname}`;
		} catch {
			canonical = url;
		}
	} else {
		canonical = getPublicMediaUrl(url) ?? url;
	}
	return buildImageProxyUrl(canonical, LISTING_CARD_PROXY_FORMAT);
}

/**
 * Card/thumbnail src — Tier A listing images load direct from Media CloudFront when possible.
 * Falls back to image-proxy resize only for legacy payloads without `*DisplayUrl` fields.
 */
export function getListingCardImageUrl(
	url?: string | null,
	displayUrl?: string | null,
): string | undefined {
	const display = displayUrl?.trim();
	const raw = url?.trim();

	const directOurMedia = (candidate: string): string | undefined => {
		if (needsImageProxy(candidate)) {
			return getDisplayImageUrl(candidate);
		}
		return getDisplayImageUrl(candidate);
	};

	// Tier A: pre-generated listing variant (`thumbnailDisplayUrl`, `media[].displayUrl`).
	if (display) {
		if (
			isSignedMediaUrl(display) ||
			isOurMediaUrl(display) ||
			getS3ObjectKeyFromUrl(display) != null
		) {
			const direct = directOurMedia(display);
			if (direct) return direct;
		}
		const resolved = getDisplayImageUrl(display);
		if (resolved) return resolved;
	}

	const ourRaw =
		raw && (isOurMediaUrl(raw) || getS3ObjectKeyFromUrl(raw) != null) ? raw : null;
	if (ourRaw) {
		const direct = directOurMedia(ourRaw);
		if (direct) return direct;
		return listingCardProxyForOurMedia(ourRaw);
	}

	if (!raw) return undefined;

	if (needsImageProxy(raw)) {
		return getDisplayImageUrl(raw);
	}

	return getDisplayImageUrl(raw);
}

/**
 * Best URL for displaying a raster image in the browser.
 * Signed API URLs and our CDN paths are used directly; third-party assets go through the proxy.
 */
export function getDisplayImageUrl(url: string | null | undefined): string | undefined {
	if (url == null || typeof url !== "string" || !url.trim()) return undefined;
	const trimmed = url.trim();

	if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
		return trimmed;
	}

	if (isSignedMediaUrl(trimmed)) {
		return trimmed;
	}

	if (needsImageProxy(trimmed)) {
		return buildImageProxyUrl(trimmed);
	}

	return getPublicMediaUrl(trimmed) ?? trimmed;
}

const PUBLIC_CDN_PREFIXES = [
	"services/thumbnails/",
	"services/gallery/",
	"blogs/display/",
	"blogs/featured-images/",
	"categories/banners/",
	"categories/featured/",
	"blog-categories/banners/",
	"system/assets/",
];

/** Unsigned CloudFront can show these. Other bucket files are private and need the image proxy. */
export function isPublicCdnMediaUrl(url: string | null | undefined): boolean {
	if (!url?.trim()) return false;
	const publicUrl = getPublicMediaUrl(url) ?? url;
	try {
		const path = decodeURIComponent(new URL(publicUrl).pathname).replace(/^\/+/, "");
		return PUBLIC_CDN_PREFIXES.some((prefix) => path.startsWith(prefix));
	} catch {
		return false;
	}
}
export const getProxiedImageUrl = getDisplayImageUrl;

/** Strip CloudFront/S3 signatures so image-proxy can mint a fresh display URL. */
export function canonicalUrlForImageProxy(url: string): string {
	const trimmed = url.trim();
	try {
		const parsed = new URL(trimmed);
		if (
			parsed.searchParams.has("Signature") ||
			parsed.searchParams.has("X-Amz-Signature") ||
			parsed.searchParams.has("Expires")
		) {
			parsed.search = "";
			return getPublicMediaUrl(parsed.toString()) ?? parsed.toString();
		}
	} catch {
		// relative or invalid URL
	}
	return getPublicMediaUrl(trimmed) ?? trimmed;
}

/** Legacy unsigned S3 URLs (cached payloads) — image-proxy fallback after direct CDN fails. */
export function needsLegacyOurMediaProxy(url: string | null | undefined): boolean {
	if (!url?.trim() || isSignedMediaUrl(url) || needsImageProxy(url)) return false;
	return getS3ObjectKeyFromUrl(url) != null;
}

/** Our S3/CloudFront media can 403 on first paint — keep /api/image-proxy as a last candidate. */
export function needsOurMediaProxyFallback(url: string | null | undefined): boolean {
	if (!url?.trim() || needsImageProxy(url)) return false;
	return isOurMediaUrl(url) || getS3ObjectKeyFromUrl(url) != null;
}
